import React from 'react';

export const LEGEND_STYLE = {
	paddingTop: '2px',
	color: 'var(--color_text_default)',
	fontSize: 12
};

const AXIS_FONT_SIZE = 13;

export const AXIS_TICK_STYLE = {
	fontSize: AXIS_FONT_SIZE,
	fill: 'var(--color_text_default)',
	fontFamily: 'var(--font_default), sans-serif'
};

export const Y_AXIS_WIDTH = 268;
export const BAR_SIZE = 21;
/** Высота одной строки: 2 строки подписи + полоса */
export const ROW_SLOT_HEIGHT = 50;
export const CHART_CHROME_HEIGHT = 72;
export const CHART_MARGIN = {top: 44, right: 24, left: 4, bottom: 16};
export const BAR_CORNER_RADIUS = 4;

const MAX_TICK_LINE_WIDTH = 26;
const MAX_TICK_LINES = 2;

export function calcChartHeight(rowCount) {
	return CHART_CHROME_HEIGHT + rowCount * ROW_SLOT_HEIGHT;
}

function getLastVisibleSegmentKey(payload, stackSegmentKeys) {
	let last = null;
	for (const key of stackSegmentKeys) {
		if ((payload[key] ?? 0) > 0) {
			last = key;
		}
	}
	return last;
}

function getFirstVisibleSegmentKey(payload, stackSegmentKeys) {
	for (const key of stackSegmentKeys) {
		if ((payload[key] ?? 0) > 0) {
			return key;
		}
	}
	return null;
}

function hasFilledSegments(payload, filledKeys) {
	return filledKeys.some((key) => (payload[key] ?? 0) > 0);
}

export function buildStackedBarPath(x, y, width, height, roundRight) {
	if (width <= 0) {
		return '';
	}
	const x0 = x;
	const y0 = y;
	const x1 = x + width;
	const y1 = y + height;
	if (!roundRight) {
		return `M${x0},${y0} L${x1},${y0} L${x1},${y1} L${x0},${y1} Z`;
	}
	const r = Math.min(BAR_CORNER_RADIUS, width / 2, height / 2);
	return [
		`M${x0},${y0}`,
		`L${x1 - r},${y0}`,
		`A${r},${r} 0 0 1 ${x1},${y0 + r}`,
		`L${x1},${y1 - r}`,
		`A${r},${r} 0 0 1 ${x1 - r},${y1}`,
		`L${x0},${y1}`,
		'Z'
	].join(' ');
}

/** Обводка по внешнему контуру: без левого штриха у стыка с заливкой, inset по strokeWidth */
export function buildOutlineStrokePath(x, y, width, height, roundRight, drawLeftEdge, strokeWidth) {
	if (width <= 0) {
		return '';
	}
	const sw = strokeWidth ?? 1.2;
	const inset = sw / 2;
	const x0 = x;
	const x1 = x + width;
	const y0 = y + inset;
	const y1 = y + height - inset;
	const innerH = y1 - y0;
	const r = roundRight ? Math.min(BAR_CORNER_RADIUS, width / 2, innerH / 2) : 0;

	if (drawLeftEdge) {
		if (!roundRight) {
			return `M${x0},${y0} L${x1},${y0} L${x1},${y1} L${x0},${y1} Z`;
		}
		return [
			`M${x0},${y0}`,
			`L${x1 - r},${y0}`,
			`A${r},${r} 0 0 1 ${x1},${y0 + r}`,
			`L${x1},${y1 - r}`,
			`A${r},${r} 0 0 1 ${x1 - r},${y1}`,
			`L${x0},${y1}`,
			'Z'
		].join(' ');
	}

	if (!roundRight) {
		return `M${x0},${y0} L${x1},${y0} L${x1},${y1} L${x0},${y1}`;
	}
	return [
		`M${x0},${y0}`,
		`L${x1 - r},${y0}`,
		`A${r},${r} 0 0 1 ${x1},${y0 + r}`,
		`L${x1},${y1 - r}`,
		`A${r},${r} 0 0 1 ${x1 - r},${y1}`,
		`L${x0},${y1}`
	].join(' ');
}

export function createStackedBarShape(dataKey, stackSegmentKeys, {outline = false, filledKeys} = {}) {
	const keysForFillCheck = filledKeys ?? stackSegmentKeys.filter((key) => key !== dataKey);

	function StackedBarShape(props) {
		const {
			x,
			y,
			width,
			height,
			payload,
			fill,
			fillOpacity,
			stroke,
			strokeWidth,
			strokeOpacity
		} = props;
		if (width == null || height == null || width <= 0) {
			return null;
		}
		const roundRight = getLastVisibleSegmentKey(payload, stackSegmentKeys) === dataKey;
		if (outline) {
			const sw = strokeWidth ?? 1.2;
			const drawLeftEdge = getFirstVisibleSegmentKey(payload, stackSegmentKeys) === dataKey
				|| !hasFilledSegments(payload, keysForFillCheck);
			const outlinePath = buildOutlineStrokePath(
				x,
				y,
				width,
				height,
				roundRight,
				drawLeftEdge,
				sw
			);
			if (!outlinePath) {
				return null;
			}
			return (
				<path
					d={outlinePath}
					fill="none"
					stroke={stroke || fill}
					strokeWidth={sw}
					strokeOpacity={strokeOpacity ?? 1}
					strokeLinejoin="round"
					strokeLinecap="butt"
				/>
			);
		}
		const d = buildStackedBarPath(x, y, width, height, roundRight);
		if (!d) {
			return null;
		}
		const hasStroke = stroke && stroke !== 'none' && (strokeWidth ?? 0) > 0;
		return (
			<path
				d={d}
				fill={fill}
				fillOpacity={fillOpacity ?? 1}
				stroke={hasStroke ? stroke : undefined}
				strokeWidth={hasStroke ? strokeWidth : undefined}
			/>
		);
	}
	return StackedBarShape;
}

/** Полная обводка бара (например, норма поверх стека) */
export function FullBarOutlineShape(props) {
	const {x, y, width, height, stroke, strokeWidth, strokeOpacity} = props;
	if (width == null || height == null || width <= 0) {
		return null;
	}
	const sw = strokeWidth ?? 1.2;
	const outlinePath = buildOutlineStrokePath(x, y, width, height, true, true, sw);
	if (!outlinePath) {
		return null;
	}
	return (
		<path
			d={outlinePath}
			fill="none"
			stroke={stroke}
			strokeWidth={sw}
			strokeOpacity={strokeOpacity ?? 1}
			strokeLinejoin="round"
			strokeLinecap="butt"
		/>
	);
}

export function wrapText(text, maxWidth) {
	const words = text.split(' ');
	const lines = [];
	let current = '';
	for (const word of words) {
		if ((current + ' ' + word).trim().length > maxWidth) {
			if (current) lines.push(current);
			current = word;
		} else {
			current = current ? current + ' ' + word : word;
		}
	}
	if (current) lines.push(current);
	return lines;
}

export function truncateLine(text, maxLength) {
	if (text.length <= maxLength) {
		return text;
	}
	return `${text.slice(0, maxLength - 1)}…`;
}

const TICK_LINE_HEIGHT = 15;

export function CustomYAxisTick({
	x,
	y,
	payload,
	activeIndex,
	hoveredIndex,
	onHoverIndex,
	onSelectIndex
}) {
	const rawLines = wrapText(payload.value, MAX_TICK_LINE_WIDTH);
	const lines = rawLines.slice(0, MAX_TICK_LINES).map((line, index) => {
		if (index === MAX_TICK_LINES - 1 && rawLines.length > MAX_TICK_LINES) {
			return truncateLine(line, MAX_TICK_LINE_WIDTH);
		}
		return line;
	});
	const index = payload?.index;
	const interactive = typeof onSelectIndex === 'function';
	const isHovered = hoveredIndex === index;
	const isActive = activeIndex === index;
	const dimmed = hoveredIndex !== undefined && !isHovered;
	const startY = y - ((lines.length - 1) * TICK_LINE_HEIGHT) / 2;

	return (
		<g
			className="chart-row-tick"
			onMouseEnter={interactive ? () => onHoverIndex?.(index) : undefined}
			onClick={interactive ? () => onSelectIndex?.(index) : undefined}
			style={{cursor: interactive ? 'pointer' : 'default'}}
		>
			{interactive && (
				<rect
					x={x - Y_AXIS_WIDTH + 2}
					y={y - BAR_SIZE / 2 - 6}
					width={Y_AXIS_WIDTH - 6}
					height={BAR_SIZE + 12}
					rx={8}
					fill={isHovered || isActive ? 'currentColor' : 'transparent'}
					fillOpacity={isHovered ? 0.08 : isActive ? 0.05 : 0}
				/>
			)}
			<text
				textAnchor="end"
				fill="var(--color_text_default)"
				fillOpacity={dimmed ? 0.45 : 1}
				fontSize={AXIS_FONT_SIZE}
				fontWeight={isActive || isHovered ? 600 : 450}
				fontFamily="var(--font_default), sans-serif"
				style={{pointerEvents: 'none'}}
			>
				{lines.map((line, i) => (
					<tspan
						key={i}
						x={x - 10}
						y={startY + i * TICK_LINE_HEIGHT}
						dominantBaseline="central"
					>
						{line}
					</tspan>
				))}
			</text>
		</g>
	);
}

export function toPercent(value, total) {
	if (!total) {
		return '0%';
	}
	return `${Math.round((value / total) * 100)}%`;
}

export function fillOpacityFor(entryId, selectedId, hoveredId) {
	const isSelected = entryId === selectedId;
	const hasHover = hoveredId !== undefined;
	const isHovered = entryId === hoveredId;
	return hasHover ? (isHovered ? 1 : 0.45) : (isSelected ? 1 : 0.8);
}
